import React from 'react';
import {
  CULTURES,
  ANIMAUX,
  alimentationsPourAnimal,
  ABREUVEMENTS,
  getSemisPourCulture,
  demandeAbreuvement,
} from '../constants';

/**
 * Sélecteur "Culture / élevage" en cascade, repris du principe de l'app Shiny :
 * le premier menu détermine les menus suivants.
 *
 *   Culture  -> Date de semis
 *   Élevage  -> Alimentation
 *               + Abreuvement si l'animal est un porcin (Porc / Truie)
 *
 * Utilisé par les trois pages qui raisonnent par production :
 * "Eau et irrigation / besoin en eau", "Comparer / eau" et
 * "Eau à 3 mois / unitaire".
 *
 * L'état est porté par la page appelante (composant contrôlé) pour qu'elle
 * puisse construire ses paramètres d'API.
 *
 * @param valeur      { type: 'culture'|'elevage', id, semis, alimentation, abreuvement }
 * @param onChange    reçoit la nouvelle valeur complète
 * @param cultures    (optionnel) liste des cultures à proposer, CULTURES par défaut
 * @param avecElevage (optionnel) false pour masquer le groupe Élevage
 */
export default function SelecteurProduction({
  valeur,
  onChange,
  cultures = CULTURES,
  avecElevage = true,
}) {
  const { type, id, semis, alimentation, abreuvement } = valeur;

  const animal = ANIMAUX.find((a) => a.id === id);
  const semisDisponibles = type === 'culture' ? getSemisPourCulture(id) : [];
  const abreuvementRequis =
    type === 'elevage' && demandeAbreuvement(animal?.label);
  // Soupe/Sec pour les porcins uniquement, le reste pour les bovins laitiers.
  const alimentationsDisponibles =
    type === 'elevage' ? alimentationsPourAnimal(animal?.idBetail) : [];

  // Le premier menu mélange cultures et animaux : on encode le type dans la
  // valeur ("culture:1184" / "elevage:VL1") pour lever l'ambiguïté entre un
  // identifiant numérique de culture et un identifiant texte d'animal.
  function changerProduction(cle) {
    const [nouveauType, nouvelId] = cle.split(':');

    if (nouveauType === 'culture') {
      const idCulture = Number(nouvelId);
      // On repositionne le semis sur le premier disponible : les dates ne sont
      // pas les mêmes d'une famille à l'autre, garder l'ancienne enverrait une
      // valeur invalide à l'API.
      const premierSemis = getSemisPourCulture(idCulture)[0]?.value ?? null;
      onChange({ type: 'culture', id: idCulture, semis: premierSemis });
      return;
    }

    const nouvelAnimal = ANIMAUX.find((a) => a.id === nouvelId);
    // L'alimentation dépend du type d'animal (Soupe/Sec = porcins,
    // le reste = bovins) : si l'alimentation en cours n'est plus valide pour
    // ce nouvel animal (changement bovin <-> porcin), on la réinitialise sur
    // la première option valide plutôt que de garder un choix incohérent.
    const alimentationsValides = alimentationsPourAnimal(nouvelAnimal?.idBetail);
    const alimentationEncoreValide = alimentationsValides.some((a) => a.id === alimentation);
    onChange({
      type: 'elevage',
      id: nouvelId,
      alimentation: alimentationEncoreValide ? alimentation : alimentationsValides[0]?.id,
      // L'abreuvement ne concerne que les porcins : on ne le transmet pas pour
      // les bovins, sinon l'API recevrait un paramètre sans objet.
      abreuvement: demandeAbreuvement(nouvelAnimal?.label)
        ? abreuvement ?? ABREUVEMENTS[0].id
        : undefined,
    });
  }

  return (
    <>
      <label className="sidebar-champ">
        <span className="sidebar-label">Culture / élevage</span>
        <select
          value={`${type}:${id}`}
          onChange={(e) => changerProduction(e.target.value)}
        >
          <optgroup label="Cultures">
            {cultures.map((c) => (
              <option key={`culture:${c.id}`} value={`culture:${c.id}`}>
                {c.label}
              </option>
            ))}
          </optgroup>
          {avecElevage && (
            <optgroup label="Élevage">
              {ANIMAUX.map((a) => (
                <option key={`elevage:${a.id}`} value={`elevage:${a.id}`}>
                  {a.label}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </label>

      {type === 'culture' && (
        <label className="sidebar-champ">
          <span className="sidebar-label">Date de semis</span>
          <select
            value={semis ?? ''}
            onChange={(e) => onChange({ ...valeur, semis: e.target.value })}
          >
            {semisDisponibles.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {type === 'elevage' && (
        <label className="sidebar-champ">
          <span className="sidebar-label">Alimentation</span>
          <select
            value={alimentation ?? ''}
            onChange={(e) =>
              onChange({ ...valeur, alimentation: Number(e.target.value) })
            }
          >
            {alimentationsDisponibles.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {abreuvementRequis && (
        <label className="sidebar-champ">
          <span className="sidebar-label">Abreuvement</span>
          <select
            value={abreuvement ?? ''}
            onChange={(e) =>
              onChange({ ...valeur, abreuvement: Number(e.target.value) })
            }
          >
            {ABREUVEMENTS.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
      )}
    </>
  );
}

/** Valeur de départ : la première culture de la liste (CULTURES par défaut). */
export function productionParDefaut(cultures = CULTURES) {
  const premiere = cultures[0];
  return {
    type: 'culture',
    id: premiere.id,
    semis: getSemisPourCulture(premiere.id)[0]?.value ?? null,
  };
}
